import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ConfirmDialog from '../ConfirmDialog';
import { removePlayerFromAllLists } from '../../../actions/saved-lists-actions';
import { showErrorToast, showSuccessToast } from '../../../actions/notification-actions';
import { PopupTypes } from '../../../utils/constants';
import { emitSavedListsChanged } from '../../../utils/savedLists';

// The ticket's data-testid contract pins this exact literal (no `modal.` namespace), matching the
// sibling SaveToListModal — the web verifier's Playwright selectors depend on it. Intentional
// deviation from CLAUDE.md rule 4; don't "fix" it to the general convention, it'll break the verifier.
// Renders the shared ConfirmDialog; the confirm button keeps its shipped `.remove-btn` suffix
// (CLAUDE.md rule 8) via ConfirmDialog's confirmTestIdSuffix.
const TESTID_PREFIX = 'remove-player-confirm-modal';
const MODAL_TITLE = 'Remove Player from Saved List';
const CANCEL_LABEL = 'Cancel';
const REMOVE_LABEL = 'Remove';
const FALLBACK_PLAYER_LABEL = 'this player';
const SUCCESS_TOAST_TITLE = 'Player Successfully Removed';
const ERROR_TOAST_TITLE = 'Something Went Wrong';
const ERROR_TOAST_MESSAGE = 'We couldn\'t remove this player from your lists. Please try again.';

const RemovePlayerConfirmModal = ({ onCloseClick }) => {
    const dispatch = useDispatch();
    const playerId = useSelector((state) => state.popups.id);
    const playerName = useSelector((state) => state.popups.playerName);
    const onSuccess = useSelector((state) => state.popups[`${PopupTypes.REMOVE_PLAYER_CONFIRM}OnSuccess`]);

    const [isRemoving, setIsRemoving] = useState(false);

    const displayName = playerName || FALLBACK_PLAYER_LABEL;

    const handleRemove = async () => {
        if (isRemoving) {
            return;
        }
        setIsRemoving(true);
        // The try wraps only the DELETE — anything thrown by the post-success work (closing,
        // toasting, the onSuccess re-fetch) must never be reported as a failed remove, which would
        // invite a member to re-confirm a removal that actually succeeded.
        try {
            await dispatch(removePlayerFromAllLists(playerId)); // NOSONAR — Redux thunk returns a promise
        } catch (error) {
            dispatch(showErrorToast({
                title: ERROR_TOAST_TITLE,
                message: ERROR_TOAST_MESSAGE,
            }));
            // Only reset on failure — on success onCloseClick() below unmounts this component via
            // PopupContainer, so resetting after that point would set state on an unmounted node.
            setIsRemoving(false);
            return;
        }

        onCloseClick();
        // Tell any locally-cached saved-list view (the hub, an open list detail) to refetch, since this
        // removal happened through the global popup and those views can't otherwise know — matches the save.
        emitSavedListsChanged();
        dispatch(showSuccessToast({
            title: SUCCESS_TOAST_TITLE,
            message: `${displayName} has been removed from your Saved Player lists.`,
        }));
        if (onSuccess) {
            onSuccess();
        }
    };

    return (
        <ConfirmDialog
            title={MODAL_TITLE}
            message={`Are you sure you want to remove ${displayName} from your Saved Player Lists?`}
            confirmLabel={REMOVE_LABEL}
            cancelLabel={CANCEL_LABEL}
            confirmTestIdSuffix="remove-btn"
            destructive
            mobileActionSheet
            isBusy={isRemoving}
            onConfirm={handleRemove}
            onCancel={onCloseClick}
            data-testid={TESTID_PREFIX}
        />
    );
};

RemovePlayerConfirmModal.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
};

export default RemovePlayerConfirmModal;
